import React, { useEffect, useRef, useState } from "react";

function CountUp({ end, prefix = "", suffix = "", decimals = 0, duration = 1800 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const t0 = performance.now();
          const tick = (t) => {
            const p = Math.min(1, (t - t0) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(end * eased);
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [end, duration]);
  const display = decimals ? val.toFixed(decimals) : Math.round(val).toLocaleString("en-IN");
  return (
    <span ref={ref} className="mono">
      {prefix}{display}{suffix}
    </span>
  );
}

const RESEARCH_NOTES = [
  {
    q: "The cost of ambiguity is measured in gigawatts and decades.",
    src: "Capexara AI Research Note 04/2026",
    tag: "Thesis",
  },
  {
    q: "Every megaproject overrun we have dissected traces back to a coordination problem, not a compute problem.",
    src: "Capexara Practice · Post-Mortems",
    tag: "Observation",
  },
  {
    q: "Seventy-five percent of lifecycle cost is locked in during FEL-1 and FEL-2. That is where AI belongs.",
    src: "Capexara AI Research Note 02/2026",
    tag: "FEL Leverage",
  },
  {
    q: "EVM is mathematically honest and operationally ceremonial. A cognitive successor is overdue.",
    src: "Capexara Practice · Controls",
    tag: "EVM",
  },
  {
    q: "Generative AI will not replace your contracts manager. It will give her twelve of her.",
    src: "Capexara Practice · Contracts",
    tag: "Contracts",
  },
  {
    q: "The true ROI of a digital twin is value-per-MW, value-per-KL, value-per-MT — never IT spend avoided.",
    src: "Capexara Practice · CFO Advisory",
    tag: "CFO Frame",
  },
  {
    q: "Advanced Work Packaging done manually is a coordination bottleneck. Done with a cognition engine, it becomes a flywheel.",
    src: "Capexara Practice · Execution",
    tag: "AWP",
  },
];

export function Problem() {
  const stats = [
    { big: 1, prefix: "$", suffix: ".6T", label: "wasted annually on capital project overruns. 77% of megaprojects exceed their original budget. 89% are delivered late. The root cause is fragmented information, not engineering.", source: "McKinsey · KPMG · Oxford Global Projects" },
    { big: 18, suffix: "%", label: "of engineering hours lost to document search. On a $500M project, that's $5-15M in rework — repeated on every project, every year, worldwide.", source: "Capexara Practice · Document Forensics" },
    { big: 0, suffix: "", label: "platforms see the full project lifecycle. Schedule in Primavera. Cost in SAP. Documents in SharePoint. Procurement in Oracle. Risk in someone's head.", source: "Capexara Audit · Global EPC Programs" },
    { big: 14, suffix: " wks", label: "for a basic capability assessment. Organizations wait 3 months and pay $50-200K for a consulting firm to deliver what should take a week.", source: "Capexara Research · Maturity Diagnostics" },
  ];
  const scrollerRef = useRef(null);
  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.75, 560), behavior: "smooth" });
  };

  return (
    <section className="problem reveal" id="problem" data-testid="problem-section">
      <div className="container">
        <div className="problem-head">
          <div className="section-label"><span className="dot" />The execution gap</div>
          <h2 className="problem-h2 display" data-testid="problem-h2">
            $106 trillion of ambition.<br />
            <span style={{ color: "var(--text-3)" }}>Running on spreadsheets and email.</span>
          </h2>
          <p style={{ marginTop: 18, maxWidth: 720, color: "var(--text-2)", fontSize: 18, lineHeight: 1.55, fontStyle: "italic" }}>
            The industry that builds the future still manages itself with tools from the past.
          </p>
        </div>
        <div className="problem-grid" data-testid="problem-stats-grid">
          {stats.map((s, i) => (
            <div className="stat-card reveal" key={i} style={{ "--d": `${i * 120}ms` }} data-testid={`stat-card-${i}`}>
              <div className="stat-num">
                <CountUp end={s.big} prefix={s.prefix || ""} suffix={s.suffix || ""} />
              </div>
              <div className="stat-label">{s.label}</div>
              <div className="stat-source mono">— {s.source}</div>
            </div>
          ))}
        </div>

        {/* Pull quote — thesis statement, intentionally unattributed */}
        <blockquote
          data-testid="problem-pullquote"
          style={{
            margin: "60px auto 0",
            maxWidth: 920,
            padding: "0 24px",
            textAlign: "center",
            fontFamily: "var(--f-display)",
            fontStyle: "italic",
            fontSize: "clamp(20px, 2.2vw, 28px)",
            lineHeight: 1.4,
            fontWeight: 400,
            letterSpacing: "-0.01em",
            color: "var(--text-2)",
            borderLeft: "none",
          }}
        >
          &ldquo;The problem isn't that capital projects lack data. It's that no
          system has been intelligent enough to{" "}
          <span style={{ color: "var(--text-1)" }}>
            reason across engineering, procurement, construction, and controls
          </span>{" "}
          &mdash; in one place.&rdquo;
        </blockquote>

        {/* Research-notes horizontal scroller */}
        <div className="research-notes reveal" data-testid="research-notes">
          <div className="research-notes-head">
            <div>
              <div className="section-label"><span className="dot" />Notes from the practice</div>
              <h3 className="research-notes-h display">
                Seven assertions <span style={{ color: "var(--text-3)" }}>we are betting the firm on.</span>
              </h3>
            </div>
            <div className="research-notes-controls">
              <button onClick={() => scrollBy(-1)} aria-label="Scroll left" data-testid="research-notes-prev" className="research-notes-btn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
              </button>
              <button onClick={() => scrollBy(1)} aria-label="Scroll right" data-testid="research-notes-next" className="research-notes-btn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
              </button>
            </div>
          </div>
          <div className="research-notes-scroller" ref={scrollerRef} data-testid="research-notes-scroller">
            {RESEARCH_NOTES.map((n, i) => (
              <article className="research-note-card" key={i} data-testid={`research-note-${i}`}>
                <div className="research-note-tag mono">{n.tag.toUpperCase()}</div>
                <p className="research-note-quote">&ldquo;{n.q}&rdquo;</p>
                <div className="research-note-src mono">— {n.src}</div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
